import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { post } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface CmdResult {
  ok: boolean;
  message: string;
  ts: number;
}

const PRESET_CMDS = [
  { cmd: 'status',         icon: 'fa-solid fa-circle-info',    label: 'Status',         desc: 'Print system health summary.' },
  { cmd: 'ping_lavalink',  icon: 'fa-solid fa-music',          label: 'Ping Lavalink',  desc: 'Check Lavalink node connectivity.' },
  { cmd: 'flush_sessions', icon: 'fa-solid fa-broom',          label: 'Flush Sessions', desc: 'Prune expired panel sessions.' },
  { cmd: 'restart',        icon: 'fa-solid fa-rotate-right',   label: 'Restart Bot',    desc: 'Soft-restart the bot process (~2 s delay).' },
];

export function BackroomCmdPage() {
  const [cmd,       setCmd]       = useState('');
  const [results,   setResults]   = useState<CmdResult[]>([]);
  const [pending,   setPending]   = useState<string | null>(null); // cmd waiting for confirm
  const [cooldown,  setCooldown]  = useState(false);
  const [executing, setExecuting] = useState(false);

  const executeCmd = async (command: string) => {
    if (!command.trim()) return;
    setExecuting(true);
    setCooldown(true);
    const id = toast.loading(`Sending: ${command}`);
    try {
      const data = await post<{ ok: boolean; message: string }>('/panel/backroom/cmd', { cmd: command.trim() });
      const result: CmdResult = { ok: data.ok, message: data.message || 'No response', ts: Date.now() };
      setResults(prev => [result, ...prev].slice(0, 50));
      toast.success(data.ok ? data.message : `Error: ${data.message}`, { id });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      const result: CmdResult = { ok: false, message: msg, ts: Date.now() };
      setResults(prev => [result, ...prev].slice(0, 50));
      toast.error(msg, { id });
    } finally {
      setExecuting(false);
      setPending(null);
      // 2-second UI rate limit — disables send button
      setTimeout(() => setCooldown(false), 2000);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cmd.trim() || cooldown) return;
    setPending(cmd.trim());
  };

  const handlePreset = (command: string) => {
    if (cooldown) return;
    setCmd(command);
    setPending(command);
  };

  const formatTs = (ts: number) =>
    new Date(ts).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <>
      {pending && (
        <ConfirmDialog
          title="Send Bot Command"
          message={
            <>
              Send command <code className="font-mono text-cyberCyan font-bold bg-cyberCyan/5 px-1.5 py-0.5 rounded">{pending}</code> to the bot?
              <br /><br />
              This executes directly. Some commands (like{' '}
              <code className="font-mono text-red-400">restart</code>) have immediate permanent effects.
            </>
          }
          confirmLabel="Send Command"
          confirmVariant="cyan"
          onConfirm={() => executeCmd(pending)}
          onCancel={() => !executing && setPending(null)}
        />
      )}

      <div className="flex flex-col gap-6 animate-fadeIn">
        {/* Header */}
        <div>
          <div className="text-[9px] font-mono tracking-[0.2em] text-slate-600 uppercase mb-1">BackRoom</div>
          <h1 className="text-2xl font-black font-display uppercase tracking-tight text-white">
            Bot <span className="bg-gradient-to-r from-cyberCyan to-blue-400 bg-clip-text text-transparent">Commands</span>
          </h1>
        </div>

        {/* WARNING BANNER */}
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-400/25 bg-amber-400/[0.06]">
          <i className="fa-solid fa-triangle-exclamation text-amber-400 text-sm mt-0.5 shrink-0" />
          <p className="text-xs font-mono text-amber-300 leading-relaxed">
            <span className="font-bold">Direct bot commands bypass all safety checks.</span>{' '}
            Each command requires confirmation before execution. Use with extreme caution.
            The <code className="text-red-400 bg-red-500/10 px-1 rounded">restart</code> command will disconnect all active sessions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input card */}
          <div className="flex flex-col gap-4">
            {/* Presets */}
            <Card variant="glass" className="p-5">
              <div className="text-[9px] font-mono font-bold tracking-[0.15em] text-slate-500 uppercase mb-3">Quick Commands</div>
              <div className="grid grid-cols-2 gap-2">
                {PRESET_CMDS.map(p => (
                  <button
                    key={p.cmd}
                    onClick={() => handlePreset(p.cmd)}
                    disabled={cooldown}
                    className="flex flex-col gap-1 p-3 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:border-cyberCyan/20 hover:bg-cyberCyan/[0.04] transition-all disabled:opacity-40 disabled:cursor-not-allowed text-left"
                  >
                    <div className="flex items-center gap-2">
                      <i className={`${p.icon} text-[10px] text-cyberCyan/70`} />
                      <span className="text-[10px] font-bold text-white">{p.label}</span>
                    </div>
                    <span className="text-[8px] font-mono text-slate-600">{p.desc}</span>
                  </button>
                ))}
              </div>
            </Card>

            {/* Custom input */}
            <Card variant="glass" className="p-5">
              <div className="text-[9px] font-mono font-bold tracking-[0.15em] text-slate-500 uppercase mb-3">Custom Command</div>
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="text"
                  value={cmd}
                  onChange={e => setCmd(e.target.value)}
                  placeholder="e.g. status"
                  className="input-base flex-1"
                  disabled={executing}
                />
                <Button
                  type="submit"
                  variant="cyan"
                  size="sm"
                  loading={executing}
                  disabled={!cmd.trim() || cooldown}
                >
                  <i className="fa-solid fa-paper-plane" />
                  Send
                </Button>
              </form>
              {cooldown && !executing && (
                <p className="text-[9px] font-mono text-amber-400 mt-2">
                  <i className="fa-solid fa-clock mr-1" />Rate limit: please wait...
                </p>
              )}
            </Card>
          </div>

          {/* Result history */}
          <Card variant="dark" className="relative p-5">
            <div className="text-[9px] font-mono font-bold tracking-[0.15em] text-slate-500 uppercase mb-3">
              Result History
            </div>
            <div className="flex flex-col gap-2 max-h-96 overflow-y-auto scrollbar-thin">
              {results.length === 0 && (
                <p className="text-[10px] font-mono text-slate-600 italic">No commands sent yet.</p>
              )}
              {results.map((r, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-xl border text-[10px] font-mono
                    ${r.ok
                      ? 'bg-cyberGreen/[0.04] border-cyberGreen/15 text-slate-300'
                      : 'bg-red-500/[0.05] border-red-500/15 text-red-300'
                    }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <i className={`fa-solid ${r.ok ? 'fa-circle-check text-cyberGreen' : 'fa-circle-xmark text-red-400'} text-[10px]`} />
                    <span className="text-slate-500">{formatTs(r.ts)}</span>
                  </div>
                  <span className="leading-relaxed">{r.message}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
